import { useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { Botao, Falha, cx } from "../../components/ui.js";

/**
 * O pedido enviado ao modelo.
 *
 * Três origens, na ordem em que o operador realmente usa: um prompt salvo do
 * banco (mais rápido), uma sugestão da IA (quando o caso é atípico), ou escrever
 * à mão. O texto final é sempre editável — nenhuma das três origens é definitiva.
 */
export function EtapaPrompt({
  geracaoId,
  valor,
  aoMudar,
}: {
  geracaoId: number | null;
  valor: string;
  aoMudar: (v: string) => void;
}) {
  const [genero, setGenero] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const generos = trpc.promptBank.genres.useQuery();
  const prompts = trpc.promptBank.list.useQuery(genero ? { genre: genero } : undefined);

  const sugerir = trpc.generations.suggestPrompt.useMutation({
    onSuccess: (r) => {
      aoMudar(r.prompt);
      setErro(null);
    },
    onError: (e) => setErro(e.message),
  });

  return (
    <div className="flex flex-col gap-5">
      <div>
        <textarea
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          rows={5}
          placeholder="Descreva a cena. Ex.: retrato em uma biblioteca com luz suave, preservando o rosto da pessoa."
          className={cx(
            "w-full resize-y rounded-peca border border-borda bg-superficie px-3 py-3",
            "text-base leading-relaxed text-texto placeholder:text-texto-fraco",
            "focus:border-brasa focus:outline-none",
          )}
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="num font-mono text-[0.75rem] text-texto-fraco">
            {valor.length} caracteres
          </span>
          {geracaoId && (
            <Botao
              tom="fantasma"
              formato="nu"
              carregando={sugerir.isPending}
              onClick={() => sugerir.mutate({ id: geracaoId })}
            >
              Sugerir com IA
            </Botao>
          )}
        </div>
      </div>

      {erro && <Falha mensagem={erro} />}

      {(generos.data?.length ?? 0) > 0 && (
        <div>
          <p className="mb-2 text-[0.75rem] font-semibold uppercase tracking-[0.13em] text-texto-fraco">
            Prompts salvos
          </p>
          <div className="mb-3 flex flex-wrap gap-2">
            <Pilula ativa={genero === null} onClick={() => setGenero(null)}>
              Todos
            </Pilula>
            {generos.data?.map((g) => (
              <Pilula key={g} ativa={genero === g} onClick={() => setGenero(g)}>
                {g}
              </Pilula>
            ))}
          </div>

          <ul className="flex flex-col gap-2">
            {prompts.data?.map((p) => (
              <li key={p.id}>
                <button
                  onClick={() => aoMudar(p.content)}
                  className={cx(
                    "w-full rounded-peca border border-borda bg-superficie px-3 py-3 text-left",
                    "transition-colors hover:border-borda-forte",
                    valor === p.content && "border-brasa",
                  )}
                >
                  <span className="block text-[0.9375rem] font-medium">{p.label}</span>
                  <span className="mt-0.5 block line-clamp-2 text-[0.8125rem] text-texto-suave">
                    {p.content}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Pilula({
  ativa,
  onClick,
  children,
}: {
  ativa: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cx(
        "rounded-full border px-3 py-1.5 text-[0.8125rem] transition-colors",
        ativa
          ? "border-brasa bg-brasa-fraca text-brasa"
          : "border-borda text-texto-suave hover:border-borda-forte",
      )}
    >
      {children}
    </button>
  );
}
